import {observer} from 'mobx-react-lite';
import React from 'react';
import AHref from "../../../components/ahref/ahref";
import {FontBody2} from "../../../components/fonts/font-body2/font-body2";
import FontBody4 from "../../../components/fonts/font-body4/font-body4";
import FontHeading4 from "../../../components/fonts/font-heading4/font-heading4";
import {FONT_COLOR, FONT_VARIANT} from "../../../components/fonts/font-props";
import IconInfo from "../../../components/icons/icon-info/icon-info";
import IconPlus from "../../../components/icons/icon-plus/icon-plus";
import IconSirenOnLight from "../../../components/icons/icon-siren-on-light/icon-siren-on-light";
import Icon from "../../../components/icons/icon/icon";
import {ButtonIcon} from "../../../components/inputs/button-icon/button-icon";
import Card from "../../../components/layouts/card/card";
import TableRow from "../../../components/table/components/table-row/table-row";
import Table from "../../../components/table/table";
import {IColumn} from "../../../components/table/table.types";
import TextTruncate from "../../../components/text-truncate/text-truncate";
import {Tooltip} from "../../../components/tooltip/tooltip";
import config from "../../../config";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import {rootContainer, TYPE} from "../../../services/container/root";
import IsMobileService from "../../../services/is-mobile-service/is-mobile-service";
import CreateRmaSettingsStore from "../stores/create-rma-settings-store";
import currentPortalStore from "../../../stores/current-portal-store";
import OrderProductStore from "../stores/order-product-store";
import './order-product-list-card.scss';
import AddItemDrawer from "./add-item-drawer";
import RmaRequestStore from '../stores/rma-request-store';
import OrderStore from '../stores/order-store';
import Currency from "../../../components/currency/currency";
import { CREATE_RMA_MODE } from '../../../services/api/create-rma-service';
import { getPlatformIconComponent } from '../../../components/icons/icon-platform/icon-platform';
import { PLATFORM_DISPLAY_NAME } from '../../../stores/platform-service-store';
import DateTimeV2, { DATETIME_VIEW } from '../../../components/datetime-v2/datetime-v2';
import { useDateTimeFormattingParams } from '../../../hooks/use-datetime-formatting-params';

interface IProps {
	rmaRequestStore: RmaRequestStore;
	orderStore: OrderStore;
    createRmaSettingsStore: CreateRmaSettingsStore;
}

interface IAddButtonProps {
    onClick: () => void;
    disabled: boolean;
}

const ButtonAddItem: React.FC<IAddButtonProps> = (({onClick, disabled}) => {
    return (
        <ButtonIcon onClick={onClick} Component={ IconPlus} disabled={disabled}/>
    );
});

const ButtonOrderLink: React.FC<{ orderStore: OrderStore }> = (({orderStore}) => {
    /** don't render platorm button-icon next to line items for non shopify platforms */
    if (currentPortalStore.platform?.getPlatformDisplayName() !== PLATFORM_DISPLAY_NAME.SHOPIFY) {
        return null;
    }

    const iconComponent = getPlatformIconComponent(currentPortalStore.platform?.getPlatformDisplayName());
    return (
        <ButtonIcon href={orderStore.getShopifyOrderLink()} target={'_blank'}  Component={iconComponent}/>
    );
});

const OrderProductListCard: React.FC<IProps> = observer(({ rmaRequestStore, orderStore, createRmaSettingsStore }) => {
    
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';
    const dateTimeParams = useDateTimeFormattingParams();
    const isIPad = rootContainer.get<IsMobileService>(TYPE.IsMobileService).isIPad();
    const showOutOfPolicyIcon = (product: OrderProductStore):boolean => { 
        return orderStore.isCreateRmaNotAllowed || (!product.isCustomItem && orderStore.isLineItemIdOutOfPolicy(product.lineItemId!))};

    let tableColumns: IColumn[] = [
        { content: '', width: {value: 16, type: 'px'} },
        { content: getTranslation('IMAGE', pageScope), width: {value: 50, type: 'px'} },
        { content: getTranslation('NAME', pageScope), width: {value: 1, type: 'fr'} },
        { content: getTranslation('QUANTITY', pageScope), width: {value: 0.2, type: 'fr'} },
        { content: getTranslation('PAID_AMOUNT_PERITEM', pageScope), width: {value: 0.4, type: 'fr'} },
        { content: getTranslation('CATALOG_PRICE', pageScope), width: {value: 0.3, type: 'fr'} },
        { content: getTranslation('FULFILLMENT_DATE', pageScope), width: {value: 0.3, type: 'fr'}},
        { content: getTranslation('STATUS', pageScope), width: {value: 0.3, type: 'fr'} },
        { content: '', width: {value: 90, type: 'px'} }
    ];

    if(isIPad){
        tableColumns = [
            { content: '', width: {value: 16, type: 'px'} },
            { content: getTranslation('IMAGE', pageScope), width: {value: 50, type: 'px'} },
            { content: getTranslation('NAME', pageScope), width: {value: 0.6, type: 'fr'} },
            { content: getTranslation('QUANTITY', pageScope), width: {value: 0.2, type: 'fr'} },
            { content: getTranslation('PAID_AMOUNT_PERITEM', pageScope), width: {value: 0.4, type: 'fr'} },
            { content: getTranslation('STATUS', pageScope), width: {value: 0.3, type: 'fr'} },
            { content: '', width: {value: 80, type: 'px'} }
        ];
    }

    const isAddItemDisabled = (lineItemId: string) => {
        return (createRmaSettingsStore.cRmaMode === CREATE_RMA_MODE.C_RMA_POLICY_LED_MODE
            && orderStore.isLineItemIdOutOfPolicy(lineItemId)) || orderStore.isCreateRmaNotAllowed;
    }

	return (
		<div className='order-product-list-card'>
			<Card>
				{/* PHASE 2: add search bar*/}

				<div className={'headline'}>
					<Tooltip content={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('ONLY_FULFILLED_ITEMS', pageScope)}</FontBody2>}><FontHeading4 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.NEUTRAL10}>{getTranslation('ADD_ITEMS_TO_RMA', pageScope)}</FontHeading4></Tooltip> <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
						{orderStore.totalProducts} {getTranslation('PURCHASED_ITEMS', pageScope)}
						{orderStore.returnedProducts ? ' / ' + orderStore.returnedProducts + ' ' + getTranslation('RETURNED_ITEMS', pageScope): ''}
						{orderStore.removedProducts ? ' / ' + orderStore.removedProducts  + ' ' + getTranslation('REMOVED_ITEMS', pageScope): ''}
					</FontBody2>
				</div>
				<Table columns={tableColumns}>
					{
                        !!orderStore &&
                        orderStore.productList.map((product: OrderProductStore) => {
                            let compareAtPrice = <></>;
                            if(product.compareAtPrice() !== null){
                                compareAtPrice = (
                                    <FontBody2 variant={FONT_VARIANT.LINE_TROUGH}>
                                        <Currency currencyCode={product.paymentCurrencyCode} amount={product.compareAtPrice()!}/>
                                    </FontBody2>
                                );
                            }
                            let tableRow=[
                                showOutOfPolicyIcon(product) ? <Tooltip
                                content={
                                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                        {getTranslation('ITEM_OUT_OF_POLICY_INDICATOR_TEXT', pageScope)}
                                    </FontBody2>
                                }
                            >
                                <Icon Component={IconSirenOnLight} />
                            </Tooltip> : <></>,
                                <img alt="" className="product-image" src={product.imageUrl}/>,
                                <>
                                    <div>
                                        <TextTruncate>
                                            <AHref href={product.variantUrl} target={'_blank'}>
                                                <FontBody2 color={FONT_COLOR.PRIMARY2}>{product.name}</FontBody2>
                                            </AHref>
                                        </TextTruncate>
                                    </div>
                                    <TextTruncate>
                                        <FontBody2 color={FONT_COLOR.NEUTRAL3}>{product.variantTitle}</FontBody2>
                                    </TextTruncate>
								</>,
								<FontBody2 color={FONT_COLOR.PRIMARY2}>{rmaRequestStore.getOrderProductAvailableQuantity(product)}</FontBody2>,
								<FontBody2 color={FONT_COLOR.PRIMARY2}>
                                    <Currency currencyCode={product.paymentCurrencyCode} amount={rmaRequestStore.getOrderProductTotalPaidAmount(product)}/>
									{ rmaRequestStore.getOrderProductTotalPaidAmount(product) !== product.paidAmount && rmaRequestStore.getOrderProductAvailableQuantity(product) > 0 &&
										<>
											/
											<FontBody4>
                                                <Currency currencyCode={product.paymentCurrencyCode} amount={product.paidAmount}/>
                                            </FontBody4>
										</>
                                    }
								</FontBody2>,
								<FontBody2 color={FONT_COLOR.PRIMARY2}>
                                    <div className={'compare-at-price-flex'}>
                                        <Currency currencyCode={product.paymentCurrencyCode} amount={product.catalogPrice}/>
                                        {compareAtPrice}
                                    </div>
                                </FontBody2>,
                                <FontBody2 color={FONT_COLOR.PRIMARY2}>
                                    {product.fulfillmentDate ? <DateTimeV2 {...dateTimeParams} utcDateString={product.fulfillmentDate} dateTimeView={DATETIME_VIEW.DATE_ONLY} /> : ''}
                                </FontBody2>,
								<span className={product.status.class + ' chip'}>{getTranslation(product.status.textTranslationKey, pageScope)}</span>,
								product.rmaId
									//if
									? <AHref href={config.apiUrl + '/dashboard/returns/?rmaid=' + product.rmaId} target={'_blank'}>RMA{product.rmaId}</AHref>
                                    :
                                    //else if
                                    product.isCustomItem
                                        ?
                                        <Tooltip content={
                                            <div className={'order-product-list-card-tooltip'}>
                                                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('CUSTOM_ITEM_PREVENTION', pageScope)}</FontBody2>
                                            </div>
                                        }>
                                            <IconInfo/>
                                        </Tooltip>
                                    :
                                    //else if
                                    product.hasBxGy
                                        ?
                                        <Tooltip content={
                                            <div className={'order-product-list-card-tooltip'}>
                                                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('BXGY_PREVENTION', pageScope)}</FontBody2>
                                            </div>
                                        }>
                                            <IconInfo/>
                                        </Tooltip>
                                    : //else
									<>
                                        { currentPortalStore.allowUnfulfilled || (product.isFulfilled() && !product.isShopifyReturned) ?
                                            <ButtonAddItem onClick={() => product.setShowAddToRma(true)} disabled={isAddItemDisabled(product.lineItemId!)}/>
                                            : <ButtonOrderLink orderStore={orderStore}/>
                                        }
										{ product.showAddToRma &&
											<AddItemDrawer request={rmaRequestStore} createRmaSettingsStore={createRmaSettingsStore} requestProduct={rmaRequestStore.getProductRequestToEdit(product)} closeAction={()=>{product.setShowAddToRma(false)}}/>
										}
									</>
                                //endif
                            ];
                            if(isIPad){
                                tableRow.splice(5,2);
                            }
                                return <TableRow key={product.uuid}
                                              disabled={rmaRequestStore.getOrderProductAvailableQuantity(product) === 0}
                                              cells={tableRow}
                                    />
                                
                            }
                        )}
				</Table>
			</Card>
		</div>
	)
});

export default OrderProductListCard;